import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { addBookmark, fetchBookmarkIds, removeBookmark } from '../../api/clientBookmarks';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { loginRedirectForBook } from '../../utils/bookingAuth';

const BookmarkButton = ({ tenantId, className = '', size = 'md' }) => {
  const { t } = useTranslation();
  const { isAuthenticated, role, loading } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const isClient = isAuthenticated && role === 'CLIENT';

  const { data } = useQuery({
    queryKey: ['client-bookmark-ids'],
    queryFn: fetchBookmarkIds,
    enabled: isClient,
    staleTime: 30_000,
  });

  const saved = (data?.tenantIds || []).includes(String(tenantId));

  const toggleMut = useMutation({
    mutationFn: () => (saved ? removeBookmark(tenantId) : addBookmark(tenantId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-bookmark-ids'] });
      queryClient.invalidateQueries({ queryKey: ['client-bookmarks'] });
      showToast(saved ? t('client.bookmarks.removed') : t('client.bookmarks.saved'), 'success');
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const iconSize = size === 'sm' ? 'text-base' : 'text-xl';
  const pad = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';

  if (loading) return null;

  if (!isClient) {
    return (
      <Link
        to={loginRedirectForBook(`/explore/${tenantId}`)}
        className={`inline-flex ${pad} items-center justify-center rounded-full border border-zinc-300 bg-white/90 text-zinc-500 shadow-sm backdrop-blur hover:text-glow-500 dark:border-zinc-700 dark:bg-zinc-900/90 ${className}`}
        aria-label={t('client.bookmarks.saveLogin')}
        title={t('client.bookmarks.saveLogin')}
      >
        <span className={iconSize}>♡</span>
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled={toggleMut.isPending}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleMut.mutate();
      }}
      className={`inline-flex ${pad} items-center justify-center rounded-full border border-zinc-300 bg-white/90 shadow-sm backdrop-blur transition hover:scale-105 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900/90 ${
        saved ? 'text-rose-500' : 'text-zinc-500 hover:text-rose-400'
      } ${className}`}
      aria-label={saved ? t('client.bookmarks.remove') : t('client.bookmarks.save')}
      title={saved ? t('client.bookmarks.remove') : t('client.bookmarks.save')}
    >
      <span className={iconSize}>{saved ? '♥' : '♡'}</span>
    </button>
  );
};

export default BookmarkButton;
